import { styled } from '@mui/material/styles';
import { Icon } from '../../../atoms';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const Overlay = styled('div')(() => ({
    width: '100%',
    height: '100%',
    position: 'fixed',
    background: 'transparent',
    top: 0,
    left: 0,
    zIndex: 120
}));

const Container = styled('div')<{ top?: number | string; right?: number | string }>(
    ({ top = 0, right = 0, theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top,
        right,
        width: '316px',
        maxWidth: 'calc(100vw - 32px)',
        boxShadow: '-4px 4px 24px 0px rgba(0, 8, 31, 0.12), 0px 4px 2px 0px rgba(0, 8, 31, 0.04)',
        border: `1px solid ${theme.palette.semantic.bd.subtle}`,
        padding: '40px',
        background: theme.palette.semantic.bg.default
    })
);

const CloseButton = styled('button')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    background: 'transparent',
    cursor: 'pointer',
    border: 'none',
    top: 26,
    right: 26,
    position: 'absolute',
    '> svg': {
        width: '12px',
        height: '12px'
    }
}));

type Props = PropsWithChildren<{
    onClose(): void;
    parentId?: string;
    isVisible: boolean;
    className?: string;
}>;

export const NavDropdown = ({ children, onClose, parentId, isVisible = false, className }: Props) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isVisible) {
            return;
        }
        // Remember the trigger so focus returns to it when the dropdown closes.
        const trigger =
            (parentId ? document.getElementById(parentId) : null) ?? (document.activeElement as HTMLElement | null);
        // Move focus into the revealed content instead of leaving it on the trigger.
        const container = containerRef.current;
        const focusTarget = container?.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        (focusTarget ?? container)?.focus();
        return () => {
            trigger?.focus?.();
        };
    }, [isVisible, parentId]);

    const getPosition = () => {
        if (parentId === 'nav-tenant-button-login') {
            return {
                top: '45px',
                right: '20px'
            };
        }
        const parent = document.getElementById(parentId!);

        if (!parent) {
            return;
        }

        const { offsetLeft, offsetWidth } = parent;

        // Anchor below the header bar, not the immediate list-item wrapper.
        const navBar = parent.closest('nav');
        const top = ~~(navBar?.offsetHeight ?? parent.parentElement?.offsetHeight)! + 10;
        const right = ~~document.body.offsetWidth! - ~~offsetLeft! - ~~offsetWidth!;

        return {
            top,
            right
        };
    };

    if (!isVisible) {
        return null;
    }

    return createPortal(
        <Overlay onClick={onClose}>
            <Container
                ref={containerRef}
                tabIndex={-1}
                className={className}
                {...getPosition()}
                onClick={(e) => e.stopPropagation()}
            >
                <CloseButton type="button" onClick={onClose} aria-label={t('CLOSE_LABEL')}>
                    <Icon id="close" />
                </CloseButton>

                {children}
            </Container>
        </Overlay>,
        document.getElementById('root')! || document.getElementById('storybook-root')!
    );
};
